import React, { useState, useEffect } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../../modules/auth'
import { useIntl } from 'react-intl'
import { PageDataProvider } from '../../../_metronic/layout/core'
import { useLang, setLanguage } from '../../../_metronic/i18n/Metronici18n'
import LanguageSelector from '../../modules/auth/components/LanguageSelector'
import { getAsync } from '../../../_metronic/helpers/httpClient'
import { toAbsoluteUrl } from '../../../_metronic/helpers'
import PolicyChecker from '../../../_metronic/helpers/components/PolicyChecker'
import { SystemPolicyChecker } from '../../../_metronic/helpers/components/SystemPolicyChecker'
import { enumPolicies } from '../../../_metronic/helpers/enumPolicies'
import { enumSystemPolicyType } from '../../../_metronic/helpers/enumSystemPolicyType'
import { KioskRouteGuard } from './KioskRouteGuard'

const KioskLayout: React.FC = () => {
  const { currentUser, logout } = useAuth()
  const navigate = useNavigate()
  const intl = useIntl()
  const lang = useLang()
  const [languages, setLanguages] = useState<any[]>([])

  useEffect(() => {
    const requestLanguages = async () => {
      try {
        const response = await getAsync("/languages")
        if (response && response.data) {
          setLanguages(response.data.map((item: any) => {
            return ({
              lang: item.code,
              name: item.name,
              flag: toAbsoluteUrl(item.icon),
            })
          }))
        }
      } catch (error) {
        console.error(error)
      }
    }
    requestLanguages();
  }, [])

  const handleLanguageChange = (language: any) => {
    setLanguage(language.lang)
  };

  const handleSwitchUser = () => {
    logout()
    navigate('/kiosk')
  }

  return (
    <PageDataProvider>
      <div className="d-flex flex-column flex-root" id="kt_app_root" style={{ minHeight: '100vh' }}>
        {/* Kiosk Top Bar */}
        <div
          className="d-flex align-items-center justify-content-between px-5 py-3 shadow-sm"
          style={{
            backgroundColor: '#1e1e2d',
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          {/* Left: Navigation tabs */}
          <div className="d-flex align-items-center gap-2">
            <PolicyChecker policies={[enumPolicies.ManufacturedProductRead]}>
              <NavLink
                to="/kiosk/production"
                className={({ isActive }) =>
                  `btn btn-sm fw-bold px-5 py-3 ${isActive ? 'btn-primary' : 'btn-dark text-gray-300 border border-gray-700'}`
                }
              >
                <i className="ki-duotone ki-wrench fs-4 me-2">
                  <span className="path1"></span>
                  <span className="path2"></span>
                </i>
                {intl.formatMessage({ id: 'PRODUCTION.MANAGEMENT.PRODUCTION_TITLE' })}
              </NavLink>
            </PolicyChecker>

            <SystemPolicyChecker policyId={enumSystemPolicyType.StockControl}>
              <PolicyChecker policies={[enumPolicies.StockRead, enumPolicies.StockEdit, enumPolicies.StockWrite]}>
                <NavLink
                  to="/kiosk/stocks"
                  className={({ isActive }) =>
                    `btn btn-sm fw-bold px-5 py-3 ${isActive ? 'btn-primary' : 'btn-dark text-gray-300 border border-gray-700'}`
                  }
                >
                  <i className="ki-duotone ki-cube-3 fs-4 me-2">
                    <span className="path1"></span>
                    <span className="path2"></span>
                    <span className="path3"></span>
                  </i>
                  {intl.formatMessage({ id: 'STOCK.MANAGEMENT.STOCKS_TITLE' })}
                </NavLink>
              </PolicyChecker>
            </SystemPolicyChecker>
          </div>

          {/* Right: User info + Switch User */}
          <div className="d-flex align-items-center gap-4">
            <LanguageSelector
              languages={languages}
              currentLanguageCode={lang}
              onLanguageChange={handleLanguageChange}
              className='me-0'
              buttonClassName='btn-color-white'
            />

            {currentUser && (
              <div className="d-flex align-items-center">
                <div className="symbol symbol-35px symbol-circle me-3">
                  <img
                    src={currentUser.avatarLink || '/media/svg/avatars/blank.svg'}
                    alt="avatar"
                    onError={(e: any) => { e.target.src = '/media/svg/avatars/blank.svg' }}
                    style={{ objectFit: 'cover' }}
                  />
                </div>
                <span className="text-white fw-bold fs-6">
                  {currentUser.name} {currentUser.surname}
                </span>
              </div>
            )}

            <button
              className="btn btn-sm btn-light-danger fw-bold px-5 py-3"
              onClick={handleSwitchUser}
            >
              <i className="ki-duotone ki-exit-left fs-4 me-2">
                <span className="path1"></span>
                <span className="path2"></span>
              </i>
              {intl.formatMessage({ id: 'KIOSK.SWITCH_USER', defaultMessage: 'Switch User' })}
            </button>
          </div>
        </div>

        {/* Page Content — full width, no sidebar */}
        <div className="flex-column-fluid d-flex flex-column" style={{ backgroundColor: '#f5f8fa' }}>
          <div className="p-5 p-md-8 flex-grow-1">
            <KioskRouteGuard>
              <Outlet />
            </KioskRouteGuard>
          </div>
        </div>
      </div>
    </PageDataProvider>
  )
}

export { KioskLayout }
